import { Instagram, MapPin, Mail, MessageCircle, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { trackWaClick } from "@/lib/api";

export default function ContactSection() {
  const { content } = useSite();
  const c = content?.contact || {};
  const cs = content?.contact_section || {};
  const labels = content?.contact_labels || {};
  const waNumber = (c.whatsapp || "3624723836").replace(/\D/g, "");
  const waLink = `https://wa.me/54${waNumber}?text=${encodeURIComponent(c.whatsapp_message || "Hola AUREX")}`;

  return (
    <section id="contacto" className="relative mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono-jb text-xs uppercase tracking-[0.35em] text-[color:var(--aurex-accent-soft)]">
              {cs.overline || "Contacto"}
            </span>
            <h2 className="mt-5 font-display text-5xl font-extrabold tracking-tight text-[color:var(--aurex-text)] sm:text-6xl">
              {cs.title || "Hablemos."}
            </h2>
            <p className="mt-5 max-w-md text-[color:var(--aurex-muted)]">{cs.subtitle}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <a
              data-testid="contact-whatsapp-btn"
              href={waLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWaClick("contact-cta")}
              className="group mt-9 inline-flex items-center gap-3 rounded-full copper-bg px-8 py-4 text-base font-semibold text-[color:var(--aurex-on-accent)] transition-transform duration-300 hover:scale-[1.04]"
            >
              <MessageCircle size={20} />
              Escribinos por WhatsApp
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.12}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackWaClick("contact-card")}
                className="group rounded-2xl border border-white/10 bg-[color:var(--aurex-surface)] p-7 transition-colors duration-300 hover:border-[color:var(--aurex-accent)]"
              >
                <MessageCircle className="text-[color:var(--aurex-accent-soft)]" size={24} />
                <div className="mt-5 font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[color:var(--aurex-muted)]">{labels.whatsapp || "WhatsApp"}</div>
                <div className="mt-1 font-display text-xl font-bold text-[color:var(--aurex-text)]">{c.whatsapp_display || c.whatsapp}</div>
              </a>

              <a
                href={c.instagram_url || `https://instagram.com/${c.instagram}`}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram"
                className="group rounded-2xl border border-white/10 bg-[color:var(--aurex-surface)] p-7 transition-colors duration-300 hover:border-[color:var(--aurex-accent)]"
              >
                <Instagram className="text-[color:var(--aurex-accent-soft)]" size={24} />
                <div className="mt-5 font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[color:var(--aurex-muted)]">{labels.instagram || "Instagram"}</div>
                <div className="mt-1 font-display text-xl font-bold text-[color:var(--aurex-text)]">@{c.instagram}</div>
              </a>

              <div className="rounded-2xl border border-white/10 bg-[color:var(--aurex-surface)] p-7 sm:col-span-2">
                <MapPin className="text-[color:var(--aurex-accent-soft)]" size={24} />
                <div className="mt-5 font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[color:var(--aurex-muted)]">{labels.address || "Dirección"}</div>
                <div className="mt-1 font-display text-xl font-bold text-[color:var(--aurex-text)]">{c.address}</div>
              </div>

              {c.email && (
                <a
                  href={`mailto:${c.email}`}
                  className="group rounded-2xl border border-white/10 bg-[color:var(--aurex-surface)] p-7 transition-colors duration-300 hover:border-[color:var(--aurex-accent)] sm:col-span-2"
                >
                  <Mail className="text-[color:var(--aurex-accent-soft)]" size={24} />
                  <div className="mt-5 font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[color:var(--aurex-muted)]">{labels.email || "Email"}</div>
                  <div className="mt-1 font-display text-xl font-bold text-[color:var(--aurex-text)]">{c.email}</div>
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
